<script>
import { getMemberList } from '@/api/member.js'
import MemberCreateDialog from './MemberCreateDialog.vue'
import AppPagination from '@/components/common/AppPagination.vue'
import AdminShell from '@/components/admin/layout/AdminShell.vue'
import AdminFilterBar from '@/components/admin/filter/AdminFilterBar.vue'
import AdminDataTable from '@/components/admin/table/AdminDataTable.vue'
import AdminStatusBadge from '@/components/admin/table/AdminStatusBadge.vue'
import AdminLoadingState from '@/components/admin/feedback/AdminLoadingState.vue'
import AdminEmptyState from '@/components/admin/feedback/AdminEmptyState.vue'

export default {
  name: 'MemberListPage',
  components: {
    MemberCreateDialog,
    AppPagination,
    AdminShell,
    AdminFilterBar,
    AdminDataTable,
    AdminStatusBadge,
    AdminLoadingState,
    AdminEmptyState,
  },
  data() {
    return {
      search: {
        keyword: '',
        loginType: '',
        deleteYn: '',
      },
      loginTypeOptions: [
        { title: '전체', value: '' },
        { title: '사이트 로그인', value: 'SITE' },
        { title: '소셜 로그인', value: 'OAUTH' },
      ],
      deleteYnOptions: [
        { title: '전체', value: '' },
        { title: '정상', value: 'N' },
        { title: '탈퇴', value: 'Y' },
      ],
      providerLabelMap: {
        KAKAO: '카카오',
        NAVER: '네이버',
        GOOGLE: '구글',
        APPLE: '애플',
      },
      providerColorMap: {
        KAKAO: 'warning',
        NAVER: 'success',
        GOOGLE: 'error',
        APPLE: 'grey',
      },
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      createDialog: false,
    }
  },
  computed: {
    filterFields() {
      return [
        {
          key: 'keyword',
          type: 'text',
          label: '회원 검색',
          placeholder: '이름, 연락처, 이메일',
        },
        {
          key: 'loginType',
          type: 'select',
          label: '가입유형',
          options: this.loginTypeOptions,
          itemTitle: 'title',
          itemValue: 'value',
        },
        {
          key: 'deleteYn',
          type: 'select',
          label: '회원 상태',
          options: this.deleteYnOptions,
          itemTitle: 'title',
          itemValue: 'value',
        },
      ]
    },
    tableColumns() {
      return [
        { key: 'no', title: '번호', align: 'center', width: '80px' },
        { key: 'name', title: '이름', align: 'center' },
        { key: 'email', title: '이메일', align: 'center' },
        { key: 'phone', title: '연락처', align: 'center' },
        { key: 'birthDate', title: '생년월일', align: 'center', width: '120px' },
        { key: 'address', title: '주소', align: 'left' },
        { key: 'authYn', title: '인증여부', align: 'center', width: '110px' },
        { key: 'loginType', title: '가입유형', align: 'center' },
        { key: 'insertDt', title: '가입일', align: 'center', width: '120px' },
        { key: 'deleteYn', title: '상태', align: 'center', width: '90px' },
      ]
    },
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getMemberList({
          keyword: this.search.keyword || undefined,
          loginType: this.search.loginType || undefined,
          deleteYn: this.search.deleteYn || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } catch (e) {
        console.error('[MemberListPage] fetchList error:', e)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = { keyword: '', loginType: '', deleteYn: '' }
      this.page = 1
      this.fetchList()
    },
    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },
    goToDetail(item) {
      const row = item?.raw || item
      if (!row?.userCode) return
      this.$router.push(`/members/${row.userCode}`)
    },
    onMemberCreated() {
      this.createDialog = false
      this.page = 1
      this.fetchList()
    },
    getAddress(row) {
      const address = [row.address1, row.address2].filter(Boolean).join(' ')
      return address || '-'
    },
  },
}
</script>

<template>
  <AdminShell title="회원관리">
    <template #header-actions>
      <v-btn color="primary" prepend-icon="mdi-account-plus-outline" @click="createDialog = true">
        회원 등록
      </v-btn>
    </template>

    <p class="admin-page-subtitle">가입 회원 조회 및 정보 관리</p>

    <AdminFilterBar
      :model-value="search"
      :fields="filterFields"
      @update:model-value="search = $event"
      @search="handleSearch"
      @reset="handleReset"
    />

    <div class="admin-page-card pa-4 mt-5">
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="text-subtitle-1 font-weight-bold">
          회원 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}명</v-chip>
        </div>
      </div>

      <AdminDataTable
        :columns="tableColumns"
        :items="items"
        row-key="userCode"
        :loading="loading"
        empty-text="검색 결과가 없습니다."
        @row-click="goToDetail"
      >
        <template #cell-no="{ index }">
          <span class="text-caption text-medium-emphasis">
            {{ total - (page - 1) * size - index }}
          </span>
        </template>

        <template #cell-name="{ item }">
          <span class="font-weight-medium">{{ (item?.raw || item)?.name || '-' }}</span>
        </template>

        <template #cell-email="{ item }">
          <span class="text-body-2">{{ (item?.raw || item)?.email || '-' }}</span>
        </template>

        <template #cell-phone="{ item }">
          <span class="text-body-2">{{ (item?.raw || item)?.phone || '-' }}</span>
        </template>

        <template #cell-birthDate="{ item }">
          <span class="text-caption">{{ (item?.raw || item)?.birthDate || '-' }}</span>
        </template>

        <template #cell-address="{ item }">
          <span class="text-body-2">{{ getAddress(item?.raw || item) }}</span>
        </template>

        <template #cell-authYn="{ item }">
          <AdminStatusBadge
            :status="(item?.raw || item)?.authYn === 'Y' ? 'VERIFIED' : 'UNVERIFIED'"
            :map="{
              VERIFIED: { label: '인증', tone: 'success' },
              UNVERIFIED: { label: '미인증', tone: 'warning' },
            }"
            size="x-small"
          />
        </template>

        <template #cell-loginType="{ item }">
          <div class="d-flex align-center ga-1 flex-wrap justify-center">
            <v-chip v-if="(item?.raw || item)?.siteLoginYn === 'Y'" size="x-small" color="primary" variant="tonal" label>
              사이트
            </v-chip>
            <v-chip
              v-for="provider in ((item?.raw || item)?.oauthProviders || [])"
              :key="provider"
              size="x-small"
              :color="providerColorMap[provider] || 'default'"
              variant="tonal"
              label
            >
              {{ providerLabelMap[provider] || provider }}
            </v-chip>
            <span v-if="(item?.raw || item)?.siteLoginYn !== 'Y' && (!((item?.raw || item)?.oauthProviders || []).length)" class="text-caption text-medium-emphasis">-</span>
          </div>
        </template>

        <template #cell-insertDt="{ item }">
          <span class="text-caption">
            {{ (item?.raw || item)?.insertDt ? (item?.raw || item).insertDt.substring(0, 10) : '-' }}
          </span>
        </template>

        <template #cell-deleteYn="{ item }">
          <AdminStatusBadge
            :status="(item?.raw || item)?.deleteYn === 'Y' ? 'WITHDRAWN' : 'ACTIVE'"
            :map="{
              ACTIVE: { label: '정상', tone: 'success' },
              WITHDRAWN: { label: '탈퇴', tone: 'default' },
            }"
            size="x-small"
          />
        </template>

        <template #loading>
          <div class="pa-6">
            <AdminLoadingState text="회원 목록을 불러오는 중..." />
          </div>
        </template>

        <template #empty>
          <div class="pa-4">
            <AdminEmptyState title="검색 결과가 없습니다." />
          </div>
        </template>
      </AdminDataTable>

      <div class="mt-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </div>

    <MemberCreateDialog v-model="createDialog" @created="onMemberCreated" />
  </AdminShell>
</template>
